<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-title>
              <AppPageHeader
                icon="mdi-source-branch"
                title="Sale Linkage"
                subtitle="Every sale with its replacements and returns - open a sale to see what happened to each product"
                style="width: 100%"
              />
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="4">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.order_no"
                    label="Order number (sale or replacement)..."
                    prepend-inner-icon="mdi-pound"
                    clearable
                    @click:clear="(filter.order_no = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col md="4">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Customer name, phone or product..."
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col md="4">
                  <v-select
                    solo
                    rounded
                    v-model="filter.links"
                    :items="linkOptions"
                    item-text="text"
                    item-value="value"
                    label="Show"
                    prepend-inner-icon="mdi-filter-variant"
                    @input="(page = 1), getData()"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
              </section>
              <v-simple-table v-else class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Order #</th>
                      <th class="text-left">Customer</th>
                      <th class="text-left">Date</th>
                      <th class="text-left">Items</th>
                      <th class="text-right">Amount</th>
                      <th class="text-left">Linked</th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="sales.length">
                    <template v-for="sale in sales">
                      <tr :key="`s_${sale.id}`" class="clickable-row" @click="toggle(sale)">
                        <td class="font-weight-bold">{{ sale.order_no }}</td>
                        <td>
                          {{ sale.customer_name }}
                          <div v-if="sale.customer_phone && sale.customer_phone !== '9999999999'" class="text-caption grey--text">
                            {{ sale.customer_phone }}
                          </div>
                        </td>
                        <td>{{ sale.created_at | niceDate }}</td>
                        <td>{{ sale.items_count }}</td>
                        <td class="text-right mono-figure">₹{{ money(sale.final_amount) }}</td>
                        <td>
                          <v-chip v-if="sale.replacements_count" x-small color="blue" text-color="white" class="mr-1">
                            {{ sale.replacements_count }} replacement{{ sale.replacements_count === 1 ? "" : "s" }}
                          </v-chip>
                          <v-chip v-if="sale.returns_count" x-small color="orange" text-color="white">
                            {{ sale.returns_count }} return{{ sale.returns_count === 1 ? "" : "s" }}
                          </v-chip>
                          <span v-if="!sale.replacements_count && !sale.returns_count" class="grey--text">—</span>
                        </td>
                        <td class="text-right action_th" @click.stop>
                          <RowActions :order-id="sale.id" :order-no="sale.order_no" @invoice="downloadInvoice" />
                          <v-btn icon small @click="toggle(sale)">
                            <v-icon>{{ expanded[sale.id] ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                      <tr v-if="expanded[sale.id]" :key="`d_${sale.id}`" class="linkage-detail-row">
                        <td colspan="7" class="pa-3">
                          <v-progress-linear v-if="!linkage[sale.id]" indeterminate color="primary"></v-progress-linear>
                          <div v-else-if="linkage[sale.id].error" class="error--text">{{ linkage[sale.id].error }}</div>
                          <div v-for="group in (linkage[sale.id] && linkage[sale.id].groups) || []" :key="`g_${sale.id}_${group.product_id}`" class="linkage-group">
                            <div class="linkage-group__head">
                              <span class="font-weight-bold">{{ group.product_name }}</span>
                              <v-spacer></v-spacer>
                              <span class="linkage-stat">Sold <b>{{ group.sold }}</b></span>
                              <span class="linkage-stat">Replaced <b class="blue--text">{{ group.replaced }}</b></span>
                              <span class="linkage-stat">Returned <b class="orange--text">{{ group.returned }}</b></span>
                            </div>
                            <v-simple-table dense class="linkage-table">
                              <template v-slot:default>
                                <thead>
                                  <tr>
                                    <th>Type</th>
                                    <th>Date</th>
                                    <th>Reference</th>
                                    <th>Order</th>
                                    <th>Item</th>
                                    <th class="text-right">Qty</th>
                                    <th class="text-right">Amount</th>
                                    <th>Status</th>
                                    <th class="text-right">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-for="(e, i) in group.events" :key="`e_${sale.id}_${group.product_id}_${i}`">
                                    <td>
                                      <v-chip x-small :color="typeColors[e.type]" text-color="white">{{ typeLabels[e.type] }}</v-chip>
                                    </td>
                                    <td>{{ e.date | niceDate }}</td>
                                    <td class="font-weight-bold">{{ e.reference }}</td>
                                    <td>{{ e.order_no }}</td>
                                    <td>
                                      {{ e.item }}
                                      <span v-if="e.damaged_quantity" class="red--text text-caption"> · {{ e.damaged_quantity }} damaged</span>
                                    </td>
                                    <td class="text-right">{{ e.quantity }}</td>
                                    <td class="text-right mono-figure">{{ eventAmount(e) }}</td>
                                    <td>{{ e.status }}</td>
                                    <td class="text-right">
                                      <RowActions :order-id="e.order_id" :order-no="e.order_no" @invoice="downloadInvoice" />
                                      <!-- Only on the row that still holds replaceable pieces (the latest one) -->
                                      <v-tooltip v-if="e.replaceable > 0 && can('REPLACEMENTS.CREATE')" bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                          <v-btn
                                            v-bind="attrs"
                                            v-on="on"
                                            fab
                                            x-small
                                            class="ml-1"
                                            color="blue"
                                            dark
                                            @click="$router.push(`/replacements?order_id=${e.order_id}`)"
                                          >
                                            <v-icon small>mdi-swap-horizontal</v-icon>
                                          </v-btn>
                                        </template>
                                        <span>Replace again ({{ e.replaceable }} piece{{ e.replaceable === 1 ? "" : "s" }})</span>
                                      </v-tooltip>
                                    </td>
                                  </tr>
                                </tbody>
                              </template>
                            </v-simple-table>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="7">
                        <AppEmptyState icon="mdi-source-branch" title="No sales found" message="Sales and their replacements / returns will show up here." />
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination v-model="page" :length="paginationLength"></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import AppEmptyState from "@/components/AppEmptyState.vue";
import AppPageHeader from "@/components/AppPageHeader.vue";
import RowActions from "./RowActions.vue";

export default {
  name: "saleLinkagePage",
  components: { AppEmptyState, AppPageHeader, RowActions },
  data() {
    return {
      filter: { order_no: null, search_keyword: null, links: "all" },
      linkOptions: [
        { text: "All sales", value: "all" },
        { text: "With replacement / return", value: "linked" },
        { text: "No changes", value: "plain" },
      ],
      page: 1,
      paginationLength: 0,
      sales: [],
      isLoading: false,
      expanded: {},
      linkage: {},
      typeLabels: { sale: "Sale", replacement: "Replacement", return: "Return" },
      typeColors: { sale: "green", replacement: "blue", return: "orange" },
    };
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
  },
  methods: {
    money(value) {
      return (Number(value) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    eventAmount(e) {
      if (e.type === "replacement") return Number(e.amount) > 0 ? `+₹${this.money(e.amount)}` : "No charge";
      if (e.type === "return") return `Refund ₹${this.money(e.amount)}`;
      return `₹${this.money(e.amount)}`;
    },
    async getData() {
      this.isLoading = true;
      const params = new URLSearchParams({
        page: this.page,
        rowsPerPage: this.rowsPerPage,
        order_no: this.filter.order_no || "",
        search_keyword: this.filter.search_keyword || "",
        links: this.filter.links === "all" ? "" : this.filter.links,
      });
      await axios
        .get(`sale_linkage?${params.toString()}`)
        .then((response) => {
          this.sales = response.data.data || [];
          this.paginationLength = Math.ceil((response.data.count || 0) / this.rowsPerPage);
          this.expanded = {};
          this.linkage = {};
        })
        .catch((error) => console.log(error));
      this.isLoading = false;
    },
    async toggle(sale) {
      if (this.expanded[sale.id]) {
        this.$set(this.expanded, sale.id, false);
        return;
      }
      this.$set(this.expanded, sale.id, true);
      if (this.linkage[sale.id] && !this.linkage[sale.id].error) return;
      await axios
        .get(`sale_linkage/${sale.id}`)
        .then((response) => this.$set(this.linkage, sale.id, response.data.data))
        .catch((error) =>
          this.$set(this.linkage, sale.id, {
            error: (error.response && error.response.data.message) || "Could not load this sale's linkage.",
          })
        );
    },
    async downloadInvoice(orderId, orderNo) {
      await axios
        .get(`orders/${orderId}/pdf/download`, { responseType: "blob" })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `invoice_${orderNo || orderId}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {
          if (this.$toast) this.$toast.error("Could not download the invoice.");
        });
    },
  },
};
</script>

<style scoped>
.clickable-row {
  cursor: pointer;
}
.clickable-row:hover > td {
  background-color: #fbf7ef !important;
}
.linkage-detail-row > td {
  background-color: #fafafa;
}
.linkage-group {
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  margin-bottom: 10px;
  background: #fff;
  overflow: hidden;
}
.linkage-group__head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 14px;
  border-bottom: 1px solid #eeeeee;
}
.linkage-stat {
  font-size: 12px;
  color: #757575;
}
.linkage-table th {
  font-size: 11px !important;
  text-transform: uppercase;
}
</style>
